<template>
  <div>
    <div class="rdesc-status-row">
      <extraction-template-draft-status :review="review" />
      <action-button :disabled="!isModified" @click="onRevert">
        <v-icon left>undo</v-icon>
        Revert Changes
      </action-button>
      <action-button
        color="primary"
        :disabled="!isModified"
        @click="showPublishDialog = true"
      >
        <v-icon left class="material-icons-outlined">publish</v-icon>
        Publish Draft
      </action-button>
    </div>
    <div class="rdesc-content">
      <extraction-template-panel
        :key="panelKey"
        class="mx-12"
        :field-drafts="draftFields"
        :disable-add="actionInProgress"
        @update-fields="updateFields($event)"
        @add-heading="createField(true, $event)"
        @add-field="createField(false, $event)"
        @delete-field="deleteField"
        @revert-field="revertField"
      />
      <div class="preview-header-and-panel">
        <div
          class="preview-header grey darken-2 d-flex justify-center align-center white--text"
        >
          <v-icon color="white" class="mr-2">remove_red_eye</v-icon>
          Preview
        </div>
        <data-extraction-panel
          class="preview-panel"
          :report="emptyReport"
          :fields="previewFields"
          collapse-all
          disabled
        />
      </div>
    </div>
    <extraction-template-publish-dialog
      :open.sync="showPublishDialog"
      :disabled="actionInProgress"
      :field-drafts="draftFields"
      @publish="onPublish"
    />
  </div>
</template>

<script>
import {
  createDataExtractionField,
  DataExtractionFieldHeader,
  DataExtractionFieldText,
  DataExtractionReport,
  Review,
} from '@/models';
import { mapActions } from 'vuex';
import ActionButton from '@/components/Common/ActionButton.vue';
import ExtractionTemplatePanel from '@/components/DataExtraction/ExtractionTemplatePanel';
import DataExtractionPanel from '@/components/DataExtraction/DataExtractionPanel';
import ExtractionTemplateDraftStatus from '@/components/DataExtraction/ExtractionTemplateDraftStatus.vue';
import DataExtractionFieldDraft from '@/models/DataExtractionFieldDraft';
import { GetReviewForExtractionTemplateBuilder } from '@/graphql';
import ExtractionTemplatePublishDialog from '@/components/DataExtraction/ExtractionTemplatePublishDialog.vue';
import { deepEqualExcludingId } from '@/modules/compareContents';

export default {
  name: 'ReviewDataExtractionSettingsController',
  components: {
    ActionButton,
    ExtractionTemplatePanel,
    DataExtractionPanel,
    ExtractionTemplateDraftStatus,
    ExtractionTemplatePublishDialog,
  },
  props: {
    review: {
      type: Review,
      required: true,
    },
  },
  data: () => ({
    panelKey: 0,
    emptyReport: new DataExtractionReport(),
    showPublishDialog: false,
    promiseQueue: [],
  }),
  apollo: {
    reviewForTemplateBuilder: {
      query: GetReviewForExtractionTemplateBuilder,
      variables() {
        return {
          id: this.reviewId,
        };
      },
      update: (data) => new Review(data.review),
    },
  },
  computed: {
    reviewId() {
      return this.review.id;
    },
    actionInProgress() {
      return this.promiseQueue.length > 0;
    },
    loading() {
      return this.actionInProgress;
    },
    draftFields() {
      return (
        this.reviewForTemplateBuilder?.dataExtractionFieldDrafts ??
        this.review.dataExtractionFieldDrafts
      );
    },
    isModified() {
      return (
        this.reviewForTemplateBuilder?.extractionTemplateIsDraft() ??
        this.review.extractionTemplateIsDraft()
      );
    },
    publishedFields() {
      return (
        this.reviewForTemplateBuilder?.dataExtractionFields ??
        this.review?.dataExtractionFields
      );
    },
    previewFields() {
      return this.draftFields
        .map((x) => (x.modified ? x.updatedField : x.existingField))
        .filter((field) => !!field);
    },
  },
  methods: {
    ...mapActions({
      createFieldAction:
        'dataExtractionField/createDataExtractionField',
      updateFieldAction:
        'dataExtractionField/updateDataExtractionField',
      updateReviewDraftFieldsAction:
        'review/updateReviewDataExtractionDraftFields',
      revertExtractionTemplateDraftAction:
        'review/revertDataExtractionTemplateDraft',
      publishExtractionTemplateDraftAction:
        'review/publishDataExtractionTemplateDraft',
    }),
    async lockedAction(callback) {
      const alreadyWorking = this.promiseQueue.length > 0;
      this.promiseQueue.push(callback);
      if (alreadyWorking) {
        return;
      }

      while (this.promiseQueue.length > 0) {
        try {
          await this.promiseQueue[0]();
        } finally {
          this.promiseQueue.shift();
        }
      }
    },
    async updateFields(fields) {
      const reviewId = this.reviewId;
      const remainingDraftFields = [...this.draftFields];

      await this.lockedAction(async () => {
        const newDraftFields = [];
        let draftFieldsChanged = false;
        const dirtyFields = [];

        for (const field of fields) {
          const fieldId = field.id;
          const draftFieldIdx = remainingDraftFields.findIndex(
            (draft) =>
              draft.existingField?.id === fieldId ||
              draft.updatedField?.id === fieldId
          );
          if (draftFieldIdx === -1) {
            throw new Error(`Could not find field: ${fieldId}`);
          }
          if (draftFieldIdx !== 0) {
            draftFieldsChanged = true;
          }
          const draftField = remainingDraftFields.splice(
            draftFieldIdx,
            1
          )[0];

          const oldExistingField = draftField.existingField;
          const oldUpdatedField = draftField.updatedField;

          const existingModified =
            !oldUpdatedField &&
            !deepEqualExcludingId(oldExistingField, field);

          const updatedModified =
            oldUpdatedField &&
            !deepEqualExcludingId(oldUpdatedField, field);

          let newDraftField = draftField;
          if (existingModified || updatedModified) {
            let newUpdatedField;
            if (oldUpdatedField) {
              newUpdatedField = createDataExtractionField({
                ...field,
                id: oldUpdatedField.id,
              });
              dirtyFields.push(newUpdatedField);
            } else {
              newUpdatedField = await this.createFieldAction({
                reviewId,
                field,
              });
            }

            newDraftField = new DataExtractionFieldDraft({
              ...draftField,
              modified: true,
              updatedField: newUpdatedField,
            });
            draftFieldsChanged = true;
          }
          newDraftFields.push(newDraftField);
        }

        // Handle deletions
        for (const draftField of remainingDraftFields) {
          if (!draftField.existingField && !draftField.updatedField) {
            continue;
          }

          const newDraftField = new DataExtractionFieldDraft({
            ...draftField,
            modified: true,
            updatedField: null,
          });
          newDraftFields.push(newDraftField);
          draftFieldsChanged = true;
        }

        const updatePromises = dirtyFields.map((field) =>
          this.updateFieldAction({ reviewId, field })
        );
        await Promise.all(updatePromises);

        if (draftFieldsChanged) {
          await this.updateReviewDraftFieldsAction({
            reviewId: reviewId,
            draftFields: newDraftFields,
          });
        }
      });
    },
    async createField(isHeader, { afterDraftId, closeRow }) {
      const reviewId = this.reviewId;
      const draftFields = this.draftFields;

      await this.lockedAction(async () => {
        const field = isHeader
          ? new DataExtractionFieldHeader()
          : new DataExtractionFieldText();

        const newField = await this.createFieldAction({
          reviewId,
          field,
        });

        if (closeRow) {
          closeRow();
        }

        const draftIndex = draftFields.findIndex(
          (draft) => draft.id === afterDraftId
        );
        const insertionIndex = draftIndex + 1;

        const newDraftFields = [...draftFields];
        const newDraftField = new DataExtractionFieldDraft({
          modified: true,
          updatedField: newField,
        });
        newDraftFields.splice(insertionIndex, 0, newDraftField);

        await this.updateReviewDraftFieldsAction({
          reviewId: reviewId,
          draftFields: newDraftFields,
        });
      });
    },
    async deleteField(draftId) {
      const reviewId = this.reviewId;
      const draftFields = this.draftFields;

      await this.lockedAction(async () => {
        const newDraftFields = draftFields.map((draftField) => {
          if (draftField.id !== draftId) {
            return draftField;
          }
          return new DataExtractionFieldDraft({
            ...draftField,
            modified: true,
            updatedField: null,
          });
        });

        await this.updateReviewDraftFieldsAction({
          reviewId: reviewId,
          draftFields: newDraftFields,
        });
      });
    },
    async revertField(draftId) {
      const reviewId = this.reviewId;
      const draftFields = this.draftFields;

      await this.lockedAction(async () => {
        const newDraftFields = draftFields.map((draftField) => {
          if (
            draftField.id !== draftId ||
            !draftField.existingField
          ) {
            return draftField;
          }
          const { existingField, updatedField } = draftField;
          const newUpdated =
            updatedField &&
            createDataExtractionField({
              ...existingField,
              id: updatedField.id,
            });
          return new DataExtractionFieldDraft({
            ...draftField,
            modified: false,
            updatedField: newUpdated,
          });
        });

        await this.updateReviewDraftFieldsAction({
          reviewId: reviewId,
          draftFields: newDraftFields,
        });
      });
    },
    async onRevert() {
      const reviewId = this.reviewId;
      await this.lockedAction(async () => {
        await this.revertExtractionTemplateDraftAction(reviewId);
        this.panelKey++;
      });
    },
    async onPublish() {
      const reviewId = this.reviewId;
      try {
        await this.lockedAction(async () => {
          await this.publishExtractionTemplateDraftAction(reviewId);
        });
      } finally {
        this.showPublishDialog = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~vuetify/src/styles/styles.sass';

$preview-header-height: 48px;

.rdesc-content {
  display: grid;
  grid: 'template panel' / 1fr minmax(400px, 500px);
}

.preview-header {
  height: $preview-header-height;
}
.preview-panel {
  max-width: 500px;
  min-width: 400px;
  max-height: calc(100vh - #{$preview-header-height});
  border-left: 1px solid map-get($grey, 'lighten-2');
}
.preview-header-and-panel {
  position: sticky;
  top: 0;
  max-height: 100vh;
}

.rdesc-status-row {
  display: grid;
  grid-gap: 1rem;
  grid: 'status revert-btn publish-btn' / 1fr auto auto;
  background-color: white;
  padding: 10px 36px;
  border: 1px solid #eee;
  align-items: center;
}
</style>
